import type { ColumnDef } from "@tanstack/react-table";

import { depositIcon, rewardIcon, swapIcon } from "../../assets/images";
import type { BaseRowItem } from "../../components/data-table";
import { formatAmount } from "../../utils/FormatPerson";

export const WALLET_TABLE_LIMIT = 25;

type Tx = any;

function ordinalSuffix(day: number): string {
  switch (day) {
    case 1:
    case 21:
    case 31:
      return "st";
    case 2:
    case 22:
      return "nd";
    case 3:
    case 23:
      return "rd";
    default:
      return "th";
  }
}

function formatWalletDate(value: unknown): string {
  if (!value) return "—";
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return "—";
  const day = date.getDate();
  return `${date.toLocaleString("default", {
    month: "short",
  })} ${day}${ordinalSuffix(day)}, ${date.getFullYear()}, ${date.toLocaleString(
    "default",
    { hour: "numeric", minute: "numeric", hour12: true }
  )}`;
}

export function isWalletDebit(tx: Tx): boolean {
  return (
    tx?.transactionType === "payouts" ||
    tx?.transactionType === "deex-crypto-withdrawals"
  );
}

export function walletProviderLabel(tx: Tx): string {
  if (tx?.provider?.name) return tx.provider.name;
  if (
    tx?.transactionType === "payouts" ||
    tx?.transactionType === "deex-fiat-deposits"
  ) {
    return "Hizo";
  }
  if (tx?.transactionType === "deex-deposits") return tx?.provider?.name ?? "";
  return "Quidax";
}

/** Autoswap / Reward / Payout / Deposit label + its icon. */
export function walletActivityKind(tx: Tx): { label: string; icon: string } {
  const tt = tx?.transactionType;
  if (tt === "deex-asset-swaps") return { label: "Autoswap", icon: swapIcon };
  if (tt === "payouts" && tx?.transaction?.type === "DEEX-POINT") {
    return { label: "Reward", icon: rewardIcon };
  }
  if (tt === "payouts") return { label: "Payout", icon: depositIcon };
  return { label: "Deposit", icon: depositIcon };
}

export function walletAmountText(tx: Tx): string {
  const tt = tx?.transactionType;
  const value =
    tt === "deex-asset-swaps"
      ? `${formatAmount(tx?.from?.amount)} ${tx?.from?.asset ?? ""}`
      : tt === "deex-deposits"
      ? formatAmount(tx?.amount)
      : tt === "payouts"
      ? formatAmount(tx?.asset?.amountInUSD)
      : tt === "deex-fiat-deposits"
      ? "----"
      : formatAmount(tx?.usdtAmount);

  const unit =
    tt === "gift-card-transactions"
      ? tx?.card?.name ?? ""
      : tt === "payouts"
      ? tx?.asset?.name ?? ""
      : tx?.currency ?? "";

  return `${isWalletDebit(tx) ? "-" : "+"} ${value} ${unit}`.trim();
}

export function walletNgnText(tx: Tx): string {
  if (tx?.transactionType === "payouts" && tx?.payoutAmount != null) {
    return Number(tx.payoutAmount).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  return "----";
}

export function walletStatus(tx: Tx): string {
  return tx?.transactionType === "payouts" ? tx?.status ?? "—" : "COMPLETED";
}

function statusClass(status: string): string {
  switch (status) {
    case "PENDING":
      return "text-[#CB9200] bg-[#181818]";
    case "FAILED":
      return "text-[#b14444] bg-[#181818]";
    case "COMPLETED":
    case "SUCCESSFUL":
      return "text-[#03C668] bg-[#0E2B1F]";
    default:
      return "text-[#AEBCC6] bg-[#181818]";
  }
}

export type WalletActivityRow = BaseRowItem & {
  raw: Tx;
  transId: string;
  date: string;
};

export function buildWalletActivityRows(
  transactions: Tx[]
): WalletActivityRow[] {
  return transactions.map((tx, index) => ({
    id: tx?._id ?? `wallet-tx-${index}`,
    raw: tx,
    transId: tx?._id ?? "—",
    date: formatWalletDate(tx?.createdAt || tx?.processedAt),
  }));
}

export function createWalletActivityColumns(): ColumnDef<
  WalletActivityRow,
  unknown
>[] {
  return [
    {
      accessorKey: "type",
      header: "Type",
      cell: ({ row }) => {
        const tx = row.original.raw;
        const kind = walletActivityKind(tx);
        return (
          <div className="min-w-0">
            <span className="block text-[13px] font-medium text-white">
              {isWalletDebit(tx) ? "Debit" : "Credit"}
            </span>
            <span className="mt-0.5 flex items-center gap-1 text-[11px] capitalize text-[#869AA9]">
              <img
                src={kind.icon}
                alt=""
                className="h-[12px] w-[12px] object-contain"
              />
              {walletProviderLabel(tx) || kind.label}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "amount",
      header: "Amount",
      cell: ({ row }) => {
        const tx = row.original.raw;
        return (
          <div className="min-w-0">
            <span className="block text-[13px] text-white">
              {walletAmountText(tx)}
            </span>
            <span className="mt-0.5 block text-[11px] text-[#869AA9]">
              {walletActivityKind(tx).label}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "ngn",
      header: "Amount (NGN)",
      meta: { hideBelow: "md" as const },
      cell: ({ row }) => (
        <span className="tabular-nums text-[13px] text-[#E7EBEE]">
          {walletNgnText(row.original.raw)}
        </span>
      ),
    },
    {
      accessorKey: "transId",
      header: "Trans ID",
      meta: { hideBelow: "lg" as const },
      cell: ({ getValue }) => (
        <span className="font-mono text-[11px] text-[#869AA9]">
          {String(getValue())}
        </span>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const status = walletStatus(row.original.raw);
        return (
          <span
            className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${statusClass(
              status
            )}`}
          >
            {status}
          </span>
        );
      },
    },
    {
      accessorKey: "date",
      header: "Date",
      meta: { hideBelow: "sm" as const },
      cell: ({ getValue }) => (
        <span className="text-[12px] text-[#869AA9]">{String(getValue())}</span>
      ),
    },
  ];
}

function csvCell(value: unknown): string {
  const s = value == null ? "" : String(value);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function csvAmountTraded(tx: Tx): string {
  const tt = tx?.transactionType;
  const value =
    tt === "deex-asset-swaps"
      ? Number(tx?.to?.amount ?? 0).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : tt === "deex-deposits"
      ? tx?.amount
      : tt === "payouts"
      ? Number(tx?.asset?.amountInUSD ?? 0).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : tt === "deex-fiat-deposits"
      ? "----"
      : tx?.usdtAmount;
  const unit =
    tt === "deex-deposits" && tx?.currency === "btc" ? "BTC" : "USD";
  return `${value ?? ""} ${unit}`.trim();
}

export function buildWalletActivitiesCsv(transactions: Tx[]): string {
  const header = [
    "Name",
    "Email",
    "Date created",
    "Amount traded",
    "Amount (NGN)",
    "Provider",
    "Transaction type",
    "Transaction ID",
    "Status",
  ];
  const rows = transactions.map((tx) => [
    csvCell(
      `${tx?.userDetails?.profile?.firstName ?? ""} ${
        tx?.userDetails?.profile?.lastName ?? ""
      }`.trim()
    ),
    csvCell(tx?.userDetails?.email?.address ?? ""),
    csvCell(formatWalletDate(tx?.createdAt)),
    csvCell(csvAmountTraded(tx)),
    csvCell(walletNgnText(tx)),
    csvCell(walletProviderLabel(tx)),
    csvCell(walletActivityKind(tx).label),
    csvCell(tx?._id ?? ""),
    csvCell(walletStatus(tx)),
  ]);
  return [header.join(","), ...rows.map((r) => r.join(","))].join("\n");
}
